import { api, type BolResponse } from '../lib/apiClient'
import type { UnitDetails, UnitSearchParams, UnitSearchResult } from './types'

/**
 * Typed wrappers over the BOL2 external API.
 *
 * Response types come from @blocks-online/bol-ui via ./types, which already models
 * these endpoints for the BOL3 workbench — see bol-ui/src/components/workbench/types.ts.
 */

export type { StatementItem, UnitTicket, PortalUser } from './types'

/** Matches common_business_logic\DTO\Ticket::jsonSerialize(). */
export type Ticket = {
    id: number
    title: string | null
    description: string | null
    posted_date: string
    updated_date: string | null
    action_date: string | null
    created_by_id: number
    created_by: string | null
    created_by_type: 'owner_resident' | 'agent' | null
    priority: string | null
    priority_order: number | null
    status_description: string | null
    visibility_description: string | null
    is_open: boolean
}

export type SearchBy = UnitSearchParams['searchBy']

/** POST /api/v1/core/unit-search */
export async function unitSearch(
    params: UnitSearchParams,
    signal?: AbortSignal,
): Promise<UnitSearchResult[]> {
    const { data } = await api.post<BolResponse<UnitSearchResult[]>>(
        '/core/unit-search',
        {
            search_by: params.searchBy,
            filter_value: params.filterValue,
            ...(params.blockId !== undefined && { block_id: params.blockId }),
            ...(params.unitId !== undefined && { unit_id: params.unitId }),
        },
        { signal },
    )
    return data.data ?? []
}

/** GET /api/v1/core/units/{unit_id}/details?block_id= */
export async function unitDetails(
    unitId: number,
    blockId: number,
    signal?: AbortSignal,
): Promise<UnitDetails> {
    const { data } = await api.get<BolResponse<UnitDetails>>(`/core/units/${unitId}/details`, {
        params: { block_id: blockId },
        signal,
    })
    return data.data
}

export type CreateTicketInput = {
    unitId: number
    blockId: number
    title: string
    description: string
}

/** POST /api/v1/core/units/{unit_id}/tickets */
export async function createUnitTicket(input: CreateTicketInput): Promise<Ticket> {
    const { data } = await api.post<BolResponse<Ticket>>(`/core/units/${input.unitId}/tickets`, {
        block_id: input.blockId,
        title: input.title,
        description: input.description,
    })
    return data.data
}
